import { Axios } from 'axios';
import {
  IProjectCreateDto, IProjectByIdDto, IProjectListDto,
  IProjectListNoPaginationDto,
  IProjectUsersEditDto,
  Project,
  IProjectEditDto,
} from 'components/Project/types/project';
import {
  ChangeResourceVisibilityDto, IGetProjectResourcesDto,
  IProjectResourceVisibilityDto, MoveResourcesDto,
} from 'components/Project/types/resource';
import { TableQueryParams, TableState, toQueryParams } from 'hooks/table-params';
import apiClient from 'services/api/axios';

class ProjectService {
  api: Axios;

  constructor(api: Axios) {
    this.api = api;
  }

  getProjects(params: TableQueryParams) {
    return this.api.get<IProjectListDto>('v1/projects', {
      params,
    });
  }

  getUsersProjects(includeSubUsers?: boolean) {
    return this.api.get<IProjectListNoPaginationDto>('v1/projects/no-pagination', {
      params: {
        sub: includeSubUsers,
      },
    });
  }

  getProjectById(id: number) {
    return this.api.get<IProjectByIdDto>(`v1/projects/${id}`);
  }

  createProject(dto: IProjectCreateDto) {
    return this.api.post<Pick<Project, 'id'>>('v1/projects', dto);
  }

  updateProject(id: number, dto: IProjectEditDto) {
    return this.api.patch(`v1/projects/${id}`, dto);
  }

  deleteProject(id: number) {
    return this.api.delete(`v1/projects/${id}`);
  }

  changeProjectUsers(id: number, dto: IProjectUsersEditDto) {
    return this.api.patch(`v1/projects/${id}/members`, dto);
  }

  selectProject(id: number) {
    return this.api.post('user/setProject', undefined, {
      params: {
        id,
      },
    });
  }

  getProjectResources(params: Partial<TableState>) {
    const projectId = params?.filters?.projectId;
    return this.api.get<IGetProjectResourcesDto>(`v1/projects/${projectId}/resources`, {
      params: { ...toQueryParams(params), projectId: undefined },
    });
  }

  moveResources(projectId: number, dto: MoveResourcesDto) {
    return this.api.patch(`v1/projects/${projectId}/move-resources`, dto);
  }

  changeNetworkVisibility(resourceId: number, dto: ChangeResourceVisibilityDto) {
    return this.api.patch(`v1/projects/resources/${resourceId}/manage-net-visibility`, dto);
  }

  changeDnsVisibility(resourceId: number, dto: ChangeResourceVisibilityDto) {
    return this.api.patch(`v1/projects/resources/${resourceId}/manage-dns-visibility`, dto);
  }

  changeImageVisibility(resourceId: number, dto: ChangeResourceVisibilityDto) {
    return this.api.patch(`v1/projects/resources/${resourceId}/manage-image-visibility`, dto);
  }

  changeSecurityGroupVisibility(resourceId: number, dto: ChangeResourceVisibilityDto) {
    return this.api.patch(`v1/projects/resources/${resourceId}/manage-firewall-visibility`, dto);
  }

  changeKuberGroupVisibility(resourceId: number, dto: ChangeResourceVisibilityDto) {
    return this.api.patch(`v1/projects/resources/${resourceId}/manage-kuber-visibility`, dto);
  }

  changeLoadBalancerGroupVisibility(resourceId: number, dto: ChangeResourceVisibilityDto) {
    return this.api.patch(`v1/projects/resources/${resourceId}/manage-balancer-visibility`, dto);
  }

  changeVmTemplateVisibility(resourceId: number, dto: ChangeResourceVisibilityDto) {
    return this.api.patch(`v1/projects/resources/${resourceId}/manage-vm-template-visibility`, dto);
  }

  getNetworkVisibility(resourceId: number) {
    return this.api.get<IProjectResourceVisibilityDto>(`v1/projects/resources/${resourceId}/net-visibility`);
  }

  getDnsVisibility(resourceId: number) {
    return this.api.get<IProjectResourceVisibilityDto>(`v1/projects/resources/${resourceId}/dns-visibility`);
  }

  getImageVisibility(resourceId: number) {
    return this.api.get<IProjectResourceVisibilityDto>(`v1/projects/resources/${resourceId}/image-visibility`);
  }

  getSecurityGroupVisibility(resourceId: number) {
    return this.api.get<IProjectResourceVisibilityDto>(`v1/projects/resources/${resourceId}/firewall-visibility`);
  }

  getKuberVisibility(resourceId: number) {
    return this.api.get<IProjectResourceVisibilityDto>(`v1/projects/resources/${resourceId}/kuber-visibility`);
  }

  getLoadBalancerVisibility(resourceId: number) {
    return this.api.get<IProjectResourceVisibilityDto>(`v1/projects/resources/${resourceId}/balancer-visibility`);
  }

  getVmTemplateVisibility(resourceId: number) {
    return this.api.get<IProjectResourceVisibilityDto>(`v1/projects/resources/${resourceId}/vm-template-visibility`);
  }
}
export default new ProjectService(apiClient);
